import { createSlice } from '@reduxjs/toolkit';
import { UserResConstantsType } from 'src/types/user';
import { formatCodes, formatSimpleCodes, getCodesObject, translateOptions } from './functions';
import {
    activeCodes,
    boolCodes,
    boolNumberCodes,
    scanImeiSnCodes,
    activeNumberCodes,
    configItemTypeCodes,
    lineStateCodes,
    applicationMessageCodes,
    currencyCodes,
    orderTypeCodes,
    orderClaimReturnTypeCodes,
    claimErrorDepartmentCodes,
    claimErrorCategoryCodes,
    claimErrorSeverityCodes,
    orderProductFileTypeCodes,
    orderProductCustomizationTypeCodes,
    providerFileTypeCodes,
} from './constants';
import { ConstantsStateType } from 'src/types/constantsCodes';

const initialState: ConstantsStateType = {
    loading: false,
    data: {},
    objects: {},
};

export const constantsCodesSlice = createSlice({
    name: 'constantsCodes',
    initialState,
    reducers: {
        initConstants: (state, action: { type: string; payload: UserResConstantsType }) => {
            const data = action.payload;
            state.data = {
                claimReasonCodes: formatCodes(data.claimReasonCodes, 'claimReasonCodes'),
                documentTypesCodes: formatCodes(data.documentTypesCodes, 'documentTypesCodes'),
                expeditionMethodCodes: formatCodes(data.expeditionMethodCodes, 'expeditionMethodCodes'),
                handlingUnitsCodes: formatCodes(data.handlingUnitsCodes, 'handlingUnitsCodes'),
                paymentCodes: formatCodes(data.paymentCodes, 'paymentCodes'),
                productTypeCodes: formatCodes(data.productTypeCodes, 'productTypeCodes'),
                driverCompanyCodes: formatCodes(data.driverCompanyCodes, 'driverCompanyCodes'),
                productRelationCodes: formatCodes(data.productRelationCodes, 'productRelationCodes'),
                retunReasonCodes: formatCodes(data.retunReasonCodes, 'retunReasonCodes'),
                returnResolutionCodes: formatCodes(data.returnResolutionCodes, 'returnResolutionCodes'),
                shipperCodes: formatCodes(data.shipperCodes, 'shipperCodes'),
                claimStatusCodes: formatCodes(data.claimStatusCodes, 'claimStatusCodes'),
                transportStatusCodes: formatCodes(data.transportStatusCodes, 'transportStatusCodes'),
                returnStatusCodes: formatCodes(data.returnStatusCodes, 'returnStatusCodes'),
                applicationMessageNotificationTypesCodes: formatCodes(data.applicationMessageNotificationTypesCodes, 'applicationMessageNotificationTypesCodes'),
                orderStatusCodes: formatCodes(data.orderStatusCodes, 'orderStatusCodes'),
                orderMarketplacesCodes: formatCodes(data.orderMarketplacesCodes, 'orderMarketplacesCodes'),
                receivingStatusCodes: formatCodes(data.receivingStatusCodes, 'receivingStatusCodes'),
                serviceCodes: formatCodes(data.serviceCodes, 'serviceCodes'),
                shipperMessageCodes: formatCodes(data.shipperMessageCodes, 'shipperMessageCodes'),
                claimsResolverCodes: formatCodes(data.claimsResolverCodes, 'claimsResolverCodes'),
                receivingDeliveryTypeCodes: formatCodes(data.receivingDeliveryTypeCodes, 'receivingDeliveryTypeCodes'),
                receivingUrgentCodes: formatCodes(data.receivingUrgentCodes, 'receivingUrgentCodes'),
                receivingPackingTypeCodes: formatCodes(data.receivingPackingTypeCodes, 'receivingPackingTypeCodes'),
                receivingPalletCodes: formatCodes(data.receivingPalletCodes, 'receivingPalletCodes'),
                productInsetTypeCodes: formatCodes(data.productInsetTypeCodes, 'productInsetTypeCodes'),
                insertByOrderTypeCodes: formatCodes(data.insertByOrderTypeCodes, 'insertByOrderTypeCodes'),
                emailTemplateTypeCodes: formatCodes(data.emailTemplateTypeCodes, 'emailTemplateTypeCodes'),
                orderPriorityCodes: formatCodes(data.orderPriorityCodes, 'orderPriorityCodes'),
                productCompletionSheetStatusCodes: formatCodes(
                    data.productCompletionSheetStatusCodes,
                    'productCompletionSheetStatusCodes'
                ),
                stockTakingStatusCodes: formatCodes(data.stockTakingStatusCodes, 'stockTakingStatusCodes'),
                orderCancelReasonCodes: formatCodes(data.orderCancelReasonCodes, 'orderCancelReasonCodes'),
                stockTakingTypeCodes: formatCodes(data.stockTakingTypeCodes, 'stockTakingTypeCodes'),
                transportTypeCodes: formatCodes(data.transportTypeCodes, 'transportTypeCodes'),
                templateDataStructureCodes: formatCodes(data.templateDataStructureCodes, 'templateDataStructureCodes'),
                orderProductCustomsTypeCodes: formatCodes(
                    data.orderProductCustomsTypeCodes,
                    'orderProductCustomsTypeCodes'
                ),
                activeCodes: translateOptions(activeCodes),
                boolCodes: translateOptions(boolCodes),
                boolNumberCodes: translateOptions(boolNumberCodes),
                scanImeiSnCodes: translateOptions(scanImeiSnCodes),
                activeNumberCodes: translateOptions(activeNumberCodes),
                batteryTypeCodes: formatCodes(data.batteryTypeCodes, 'batteryTypeCodes'),
                configItemTypeCodes: translateOptions(configItemTypeCodes),
                currencyCodes: currencyCodes,
                lineStateCodes: translateOptions(lineStateCodes),
                applicationMessageCodes: formatCodes(applicationMessageCodes, 'applicationMessageCodes'),
                orderClaimReturnTypeCodes: translateOptions(orderClaimReturnTypeCodes),
                claimErrorDepartmentCodes: translateOptions(claimErrorDepartmentCodes),
                claimErrorCategoryCodes: translateOptions(claimErrorCategoryCodes),
                claimErrorSeverityCodes: translateOptions(claimErrorSeverityCodes),
                orderProductFileTypeCodes: translateOptions(orderProductFileTypeCodes),
                orderProductCustomizationTypeCodes: translateOptions(orderProductCustomizationTypeCodes),
                providerFileTypeCodes: translateOptions(providerFileTypeCodes),
                jobStatusCodes: formatCodes(data.jobStatusCodes, 'jobStatusCodes'),
                jobUnitCodes: formatCodes(data.jobUnitCodes, 'jobUnitCodes'),
                jobCategoryCodes: formatCodes(data.jobCategoryCodes, 'jobCategoryCodes'),
                jobDepartmentCodes: formatCodes(data.jobDepartmentCodes, 'jobDepartmentCodes'),
                jobShipperCodes: formatCodes(data.jobShipperCodes, 'jobShipperCodes'),
                carrierExtraFeeTypeCodes: formatCodes(data.carrierExtraFeeTypeCodes, 'carrierExtraFeeTypeCodes'),
                providerSystemCodes: formatCodes(data.providerSystemCodes, 'providerSystemCodes'),
                clientTypeCodes: formatCodes(data.clientTypeCodes, 'clientTypeCodes'),
                businessFieldCodes: formatCodes(data.businessFieldCodes, 'businessFieldCodes'),
                contractTermCodes: formatCodes(data.contractTermCodes, 'contractTermCodes'),
                sourceSysCodes: formatCodes(data.sourceSysCodes, 'sourceSysCodes'),
                salesRepresentativeCodes: formatCodes(data.salesRepresentativeCodes, 'salesRepresentativeCodes'),
                keyAccountManagerCodes: formatCodes(data.keyAccountManagerCodes, 'keyAccountManagerCodes'),
                transportPaidByCodes: formatCodes(data.transportPaidByCodes, 'transportPaidByCodes'),
                stockMovementTypeCodes: formatCodes(data.stockMovementTypeCodes, 'stockMovementTypeCodes'),
                receivingPriorityCodes: formatCodes(data.receivingPriorityCodes || [], 'receivingPriorityCodes'),
                receivingTypeCodes: formatCodes(data.receivingTypeCodes || [], 'receivingTypeCodes'),
                incotermsCodes: formatCodes(data.incotermsCodes || [], 'incotermsCodes'),
                providerStatusCodes: formatCodes(data.providerStatusCodes || [], 'providerStatusCodes'),
            };

            state.objects = {
                orderStatusCodes: getCodesObject(state.data.orderStatusCodes),
                orderMarketplacesCodes: getCodesObject(state.data.orderMarketplacesCodes),
                orderCancelReasonCodes: getCodesObject(state.data.orderCancelReasonCodes),
                returnResolutionCodes: getCodesObject(state.data.returnResolutionCodes),
                claimStatusCodes: getCodesObject(state.data.claimStatusCodes),
                claimReasonCodes: getCodesObject(state.data.claimReasonCodes),
                claimsResolverCodes: getCodesObject(state.data.claimsResolverCodes),
                receivingStatusCodes: getCodesObject(state.data.receivingStatusCodes),
                receivingUrgentCodes: getCodesObject(state.data.receivingUrgentCodes),
                receivingDeliveryTypeCodes: getCodesObject(state.data.receivingDeliveryTypeCodes),
                expeditionMethodCodes: getCodesObject(state.data.expeditionMethodCodes),
                handlingUnitsCodes: getCodesObject(state.data.handlingUnitsCodes),
                driverCompanyCodes: getCodesObject(state.data.driverCompanyCodes),
                productTypeCodes: getCodesObject(state.data.productTypeCodes),
                productRelationCodes: getCodesObject(state.data.productRelationCodes),
                receivingPalletCodes: getCodesObject(state.data.receivingPalletCodes),
                productInsetTypeCodes: getCodesObject(state.data.productInsetTypeCodes),
                insertByOrderTypeCodes: getCodesObject(state.data.insertByOrderTypeCodes),
                receivingPackingTypeCodes: getCodesObject(state.data.receivingPackingTypeCodes),
                currencyCodes: getCodesObject(state.data.currencyCodes),
                lineStateCodes: getCodesObject(state.data.lineStateCodes),
                returnStatusCodes: getCodesObject(state.data.returnStatusCodes),
                retunReasonCodes: getCodesObject(state.data.retunReasonCodes),
                boolNumberCodes: getCodesObject(state.data.boolNumberCodes),
                scanImeiSnCodes: getCodesObject(state.data.scanImeiSnCodes),
                activeNumberCodes: getCodesObject(state.data.activeNumberCodes),
                batteryTypeCodes: getCodesObject(state.data.batteryTypeCodes),
                paymentCodes: getCodesObject(state.data.paymentCodes),
                shipperCodes: getCodesObject(state.data.shipperCodes),
                serviceCodes: getCodesObject(state.data.serviceCodes),
                transportStatusCodes: getCodesObject(state.data.transportStatusCodes),
                transportTypeCodes: getCodesObject(state.data.transportTypeCodes),
                orderProductCustomsTypeCodes: getCodesObject(state.data.orderProductCustomsTypeCodes),
                orderProductCustomizationTypeCodes: getCodesObject(state.data.orderProductCustomizationTypeCodes),
                orderTypeCodes: getCodesObject(orderTypeCodes),
                transportPaidByCodes: getCodesObject(state.data.transportPaidByCodes),
                orderPriorityCodes: getCodesObject(state.data.orderPriorityCodes),
                sourceSysCodes: getCodesObject(state.data.sourceSysCodes),
                clientTypeCodes: getCodesObject(state.data.clientTypeCodes),
                businessFieldCodes: getCodesObject(state.data.businessFieldCodes),
                contractTermCodes: getCodesObject(state.data.contractTermCodes),
                applicationMessageCodes: getCodesObject(state.data.applicationMessageCodes),
                stockMovementTypeCodes: getCodesObject(state.data.stockMovementTypeCodes),
                receivingPriorityCodes: getCodesObject(state.data.receivingPriorityCodes),
                receivingTypeCodes: getCodesObject(state.data.receivingTypeCodes),
                incotermsCodes: getCodesObject(state.data.incotermsCodes),
                providerStatusCodes: getCodesObject(state.data.providerStatusCodes),
                claimErrorDepartmentCodes: getCodesObject(state.data.claimErrorDepartmentCodes),
                claimErrorCategoryCodes: getCodesObject(state.data.claimErrorCategoryCodes),
                claimErrorSeverityCodes: getCodesObject(state.data.claimErrorSeverityCodes),
            };
        },
        updateConstantDetailFetch: (state, action) => {
            state.loading = true;
        },
        updateConstantDetailFetchSuccess: (state, action) => {
            state.loading = false;
            const formatedConstant = formatCodes(action.payload.data, action.payload.constantKey);
            state.data[action.payload.constantKey] = formatedConstant;
            state.objects[action.payload.constantKey] = getCodesObject(formatedConstant);
        },
        updateConstantDetailFetchFailure: (state, action) => {
            state.loading = false;
        },
    },
});

export const {
    initConstants,
    updateConstantDetailFetch,
    updateConstantDetailFetchSuccess,
    updateConstantDetailFetchFailure,
} = constantsCodesSlice.actions;

export default constantsCodesSlice.reducer;
